<script lang="ts">
    import { onMount, untrack } from "svelte";
    import MenuToggleTrigger from "./parts/MenuToggleTrigger.svelte";
    import FlexibleMenuPanel from "./parts/FlexibleMenuPanel.svelte";
    import type { IMenuItem, MenuPosition } from "./FlexibleMenu.types";
    import { storageService } from "$lib/services/storage";

    /** Ключ стану меню — в одному місці, щоб читання й запис не розійшлися. */
    const menuStateKey = (key: string) => `flexibleMenu:${key}`;

    interface Props {
        items?: IMenuItem[];
        position?: MenuPosition;
        persistenceKey?: string;
        withSpacer?: boolean;
        dataTestId?: string;
        startOpen?: boolean;
    }

    let {
        items = [],
        position = "bottom",
        persistenceKey = "",
        withSpacer = true,
        dataTestId = "flexible-menu-wrapper",
        startOpen = true,
    }: Props = $props();

    // State
    // Проп називається `startOpen` — тобто це початкове значення за
    // визначенням, далі меню відкриває й закриває користувач. `untrack`
    // говорить це компілятору явно.
    let isOpen = $state(untrack(() => startOpen));
    let isMounted = $state(false);

    // Determine orientation based on position
    const isVertical = $derived(position === "left" || position === "right");

    onMount(() => {
        if (persistenceKey) {
            // Через storageService, а не localStorage напряму: ключ мусить
            // нести префікс mindstep_ (спільний origin), а сам виклик — не
            // кидати в приватному режимі, де доступ до сховища падає
            // (STORAGE-NAMESPACE-v8 § 1).
            const savedState = storageService.get(menuStateKey(persistenceKey));
            if (savedState !== null) {
                isOpen = savedState === "true";
            }
        }
        isMounted = true;
    });

    function toggle() {
        isOpen = !isOpen;
        if (persistenceKey) {
            storageService.set(menuStateKey(persistenceKey), String(isOpen));
        }
    }
</script>

{#if withSpacer}
    <div
        class="menu-spacer position-{position}"
        class:is-open={isOpen}
        aria-hidden="true"
    ></div>
{/if}

<div
    class="flexible-menu-wrapper position-{position} {isOpen
        ? 'is-open'
        : 'is-closed'}"
    data-testid={dataTestId}
>
    {#snippet toggleSnippet()}
        <MenuToggleTrigger {isOpen} {position} onToggle={toggle} />
    {/snippet}

    <FlexibleMenuPanel {isOpen} {isVertical} {position} {items} toggle={toggleSnippet} />
</div>

<style>
    /* Styles for wrapper and spacer (layout related) */
    :global(:root) {
        --menu-z-index: 1000;
        --menu-height: 80px;
    }

    .flexible-menu-wrapper {
        position: fixed;
        z-index: var(--menu-z-index);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        display: flex;
        /* FIX: Прибрано глобальний pointer-events: none для wrapper, 
           тепер це керується на рівні компонентів (Panel та Trigger), 
           щоб уникнути проблем з кліками */
        pointer-events: none;
    }

    /* --- Positioning Logic --- */

    /* Bottom */
    .flexible-menu-wrapper.position-bottom {
        bottom: 0;
        left: 0;
        right: 0;
        flex-direction: column;
        align-items: center;
    }
    .flexible-menu-wrapper.position-bottom.is-closed {
        transform: translateY(100%);
    }

    /* Top */
    .flexible-menu-wrapper.position-top {
        top: 0;
        left: 0;
        right: 0;
        flex-direction: column;
        align-items: center;
    }
    .flexible-menu-wrapper.position-top.is-closed {
        transform: translateY(-100%);
    }

    /* Left */
    .flexible-menu-wrapper.position-left {
        left: 0;
        top: 0;
        bottom: 0;
        flex-direction: row;
        align-items: center; /* Center vertically */
    }
    .flexible-menu-wrapper.position-left.is-closed {
        transform: translateX(-100%);
    }

    /* Right */
    .flexible-menu-wrapper.position-right {
        right: 0;
        top: 0;
        bottom: 0;
        flex-direction: row-reverse; /* Handle inside */
        align-items: center; /* Center vertically */
    }
    .flexible-menu-wrapper.position-right.is-closed {
        transform: translateX(100%);
    }

    /* --- Spacer Logic --- */
    .menu-spacer {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        flex-shrink: 0;
    }

    .menu-spacer.position-bottom,
    .menu-spacer.position-top {
        width: 100%;
        height: 0;
    }
    .menu-spacer.position-bottom.is-open,
    .menu-spacer.position-top.is-open {
        height: var(--menu-height);
    }

    .menu-spacer.position-left,
    .menu-spacer.position-right {
        height: 100%;
        width: 0;
    }
    .menu-spacer.position-left.is-open,
    .menu-spacer.position-right.is-open {
        width: var(--menu-height);
    }
</style>
